import {
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { InjectionToken, inject } from '@angular/core';
import { TimeoutError, catchError, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isApiRequestUrl } from './client-telemetry.interceptor';
import { classifyApiRoute } from '../services/request-instrumentation.service';

/**
 * Production measurements show medication GETs reaching 15.7s at the tail.
 * Keep the measured 30s ceiling in one provider rather than scattering timeout
 * literals across services.
 */
export const RETTX_API_GET_TIMEOUT_MS = new InjectionToken<number | null>(
  'RETTX_API_GET_TIMEOUT_MS',
  { providedIn: 'root', factory: () => 30000 }
);

export const httpTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const timeoutMs = inject(RETTX_API_GET_TIMEOUT_MS);
  if (
    timeoutMs === null ||
    req.method !== 'GET' ||
    !environment.apiUrl ||
    !isApiRequestUrl(req.url, environment.apiUrl)
  ) {
    return next(req);
  }

  const routeClass = classifyApiRoute(req.url, environment.apiUrl) ?? 'api-other';

  return next(req).pipe(
    timeout({ each: timeoutMs }),
    catchError(error => {
      if (!(error instanceof TimeoutError)) {
        return throwError(() => error);
      }

      return throwError(() => new HttpErrorResponse({
        error: {
          code: 'RETTX_API_TIMEOUT',
          routeClass,
          timeoutMs
        },
        status: 0,
        statusText: 'Request Timeout'
      }));
    })
  );
};

export interface RettxApiTimeoutDetails {
  code: 'RETTX_API_TIMEOUT';
  routeClass: string;
  timeoutMs: number;
}

export function rettxApiTimeoutDetails(
  error: unknown
): RettxApiTimeoutDetails | null {
  if (
    !(error instanceof HttpErrorResponse) ||
    error.error?.code !== 'RETTX_API_TIMEOUT'
  ) {
    return null;
  }
  return error.error as RettxApiTimeoutDetails;
}
